import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Expand, Check, Images } from "lucide-react";
import Reveal from "@/components/Reveal";
import { getLenis } from "@/lib/scroll";

export default function ResortHighlights({ resort }) {
    return (
        <section className="bg-sand py-24 sm:py-32" data-testid="resort-highlights-section">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">Por Que Escolher</p>
                    <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl">
                        Por que escolher este resort?
                    </h2>
                    <ul className="mt-8 space-y-4">
                        {resort.highlights.map((h) => (
                            <li key={h} className="flex items-start gap-3.5 text-base leading-relaxed text-stone-700">
                                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-forest-emerald/10 text-forest-emerald">
                                    <Check className="h-4 w-4" />
                                </span>
                                {h}
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="group relative h-[420px] overflow-hidden rounded-3xl">
                        <img
                            src={resort.gallery[1]?.src || resort.cardImage}
                            alt={resort.gallery[1]?.alt || resort.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export function ResortGallery({ resort }) {
    const images = resort.gallery || [];
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        if (lightbox === null) return undefined;
        getLenis()?.stop();
        const onKey = (e) => {
            if (e.key === "Escape") setLightbox(null);
            if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % images.length);
            if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            getLenis()?.start();
            window.removeEventListener("keydown", onKey);
        };
    }, [lightbox, images.length]);

    if (!images.length) return null;

    const preview = images.slice(0, 4);
    const touch = { x: 0 };
    const onTouchStart = (e) => {
        touch.x = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        const delta = e.changedTouches[0].clientX - touch.x;
        if (Math.abs(delta) > 48) {
            setLightbox((v) => (delta < 0 ? (v + 1) % images.length : (v - 1 + images.length) % images.length));
        }
    };

    return (
        <section className="bg-[#F1ECE1] py-24 sm:py-32" data-testid="resort-gallery-section">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">Galeria</p>
                    <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl lg:text-5xl" data-testid="resort-gallery-title">
                        Descubra o {resort.name}
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-stone-600">
                        {images.length} fotografias oficiais para você sentir o destino antes de chegar.
                    </p>
                </Reveal>

                <div className="mt-12 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[190px] md:grid-cols-4" data-testid="resort-gallery-grid">
                    {preview.map((img, idx) => {
                        const isMain = idx === 0;
                        const isLast = idx === preview.length - 1 && images.length > 4;
                        return (
                            <Reveal key={img.id} delay={idx * 0.08} className={isMain ? "col-span-2 row-span-2" : ""}>
                                <button
                                    type="button"
                                    data-testid={`resort-gallery-item-${img.id}`}
                                    onClick={() => setLightbox(idx)}
                                    className="group relative h-full w-full overflow-hidden rounded-2xl"
                                >
                                    <img src={img.src} alt={img.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                                    <span className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/25" />
                                    {isLast ? (
                                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-forest-deep/70 text-white backdrop-blur-[2px]" data-testid="gallery-see-all-overlay">
                                            <Images className="h-6 w-6 text-gold" />
                                            <span className="text-sm font-semibold">Ver todas as fotos</span>
                                            <span className="text-xs text-stone-300">{images.length} imagens</span>
                                        </span>
                                    ) : (
                                        <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
                                            <Expand className="h-4 w-4" />
                                        </span>
                                    )}
                                </button>
                            </Reveal>
                        );
                    })}
                </div>

                {images.length > 4 && (
                    <Reveal delay={0.2} className="mt-6 text-center">
                        <button
                            type="button"
                            data-testid="gallery-see-all-btn"
                            onClick={() => setLightbox(0)}
                            className="inline-flex items-center gap-2 rounded-full border-2 border-forest-emerald px-7 py-3 text-sm font-semibold text-forest-emerald transition-all duration-200 hover:bg-forest-emerald hover:text-white active:scale-[0.98]"
                        >
                            <Images className="h-4 w-4" />
                            Ver todas as fotos ({images.length})
                        </button>
                    </Reveal>
                )}
            </div>

            <AnimatePresence>
                {lightbox !== null && images[lightbox] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
                        onClick={() => setLightbox(null)}
                        data-testid="resort-gallery-lightbox"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Galeria de fotos ampliada"
                    >
                        <span className="absolute left-5 top-5 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white" data-testid="lightbox-counter">
                            {lightbox + 1} / {images.length}
                        </span>
                        <button
                            type="button"
                            data-testid="resort-lightbox-close-btn"
                            aria-label="Fechar galeria"
                            onClick={() => setLightbox(null)}
                            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        {images.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    data-testid="resort-lightbox-prev-btn"
                                    aria-label="Foto anterior"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightbox((v) => (v - 1 + images.length) % images.length);
                                    }}
                                    className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 sm:left-6"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                                <button
                                    type="button"
                                    data-testid="resort-lightbox-next-btn"
                                    aria-label="Próxima foto"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightbox((v) => (v + 1) % images.length);
                                    }}
                                    className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 sm:right-6"
                                >
                                    <ChevronRight className="h-5 w-5" />
                                </button>
                            </>
                        )}
                        <div
                            className="flex max-h-full max-w-full flex-col items-center"
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                            data-testid="lightbox-swipe-area"
                        >
                            <motion.img
                                key={images[lightbox].id}
                                src={images[lightbox].src}
                                alt={images[lightbox].alt}
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                className="max-h-[78vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
                                draggable={false}
                            />
                            <p className="mt-4 max-w-lg text-center text-xs text-white/80">{images[lightbox].alt}</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
